import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';

/**
 * The line above the combined history's rows that says which of them are
 * shown, what they match, and how they are sorted, as `PaginationMeta` words
 * it for the server-paged list. With nothing matching it says so instead.
 * After a search or filter change the history reads this line's sentence
 * through its own status region, since this line is not live.
 *
 * The history focuses this line after a page change with the platform's
 * `focusElement`, which makes it focusable for that moment, because
 * va-pagination drops focus to the body when a page link is activated, and a
 * screen reader then reads the new range. It is not a live region: a line
 * that is both focused and live is read twice, and a sort is announced by
 * va-table itself.
 *
 * @param {Object} props
 * @param {number} props.first The 1-based position of the first row shown.
 * @param {number} props.last The 1-based position of the last row shown.
 * @param {number} props.total How many rows there are.
 * @param {string[]} [props.filterPhrases] What the rows match, one phrase
 *   per search or filter that is set, read after the count.
 * @param {string|null} [props.sortLabel] What the rows are sorted by, or
 *   null for the loader's order.
 * @param {React.Ref<HTMLParagraphElement>} ref
 * @returns {JSX.Element}
 */
/**
 * @param {{ first: number, last: number, total: number, filterPhrases?: string[] }} props
 * @returns {string} The count and what the rows match, without the sort.
 */
const countText = ({ first, last, total, filterPhrases = [] }) => {
  if (total === 0) {
    return `No submissions ${
      filterPhrases.length > 0
        ? filterPhrases.join(' ')
        : 'match your search or filters'
    }`;
  }
  return `Showing ${first}-${last} of ${total} ${
    total === 1 ? 'submission' : 'submissions'
  }${filterPhrases.length > 0 ? ` ${filterPhrases.join(' ')}` : ''}`;
};

/**
 * @param {{ first: number, last: number, total: number, filterPhrases?: string[], sortLabel?: string|null }} props
 *   The summary's props.
 * @returns {string} The whole sentence as the line reads it, for the
 *   history's status region to announce after a search or filter change.
 */
export const summarySentence = props =>
  countText(props) +
  (props.total > 0 && props.sortLabel
    ? `, sorted by “${props.sortLabel}”`
    : '');

const CombinedHistorySummary = forwardRef(
  ({ first, last, total, filterPhrases, sortLabel }, ref) => (
    <p
      ref={ref}
      className="submissions__history-summary vads-u-margin-top--0 vads-u-margin-bottom--2"
      data-testid="combined-history-summary"
    >
      {countText({ first, last, total, filterPhrases })}
      {total > 0 && sortLabel && (
        <>
          , sorted by “<strong>{sortLabel}</strong>”
        </>
      )}
    </p>
  ),
);

CombinedHistorySummary.displayName = 'CombinedHistorySummary';

CombinedHistorySummary.propTypes = {
  first: PropTypes.number.isRequired,
  last: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  filterPhrases: PropTypes.arrayOf(PropTypes.string),
  sortLabel: PropTypes.string,
};

CombinedHistorySummary.defaultProps = {
  filterPhrases: [],
  sortLabel: null,
};

export default CombinedHistorySummary;
